import { zodResolver } from "@hookform/resolvers/zod";
import { AxiosError, isAxiosError } from "axios";
import dayjs from "dayjs";
import { useMemo, useState } from "react";
import { useForm, type Path } from "react-hook-form";
import { useIntl } from "react-intl";

import type { Traveller } from "@/features/travellers/types/types";
import {
  getTravellerValidationSchema,
  type TravellerAgeGroupConstraint,
} from "@/features/travellers/utils/validation";
import { rudderAnalytics, CommonTrackingEvents } from "@/lib/rudderstack";
import type {
  CreateTravelerBodyInputDto,
  UpdateTravelerBodyInputDto,
  ValidationExceptionOutputDto,
} from "@/types/codegen";

import type { ScannedData } from "./ScanningDrawer";

type TravelerBodyInputDto = CreateTravelerBodyInputDto &
  UpdateTravelerBodyInputDto;

export type TravellerFormValues = {
  firstName: string;
  lastName: string;
  birthDate: Date | null;
  passportNumber: string;
  emailAddress: string;
  passportIssuingDate: Date | null;
  passportExpiryDate: Date | null;
  title: "mr" | "mrs" | "miss" | "ms" | "mstr";
  phonePrefix: string;
  phoneNumber: string;
  passportNationality: "IQ";
  passportIssuingCountry: "IQ";
};

const mapTravellerToFormValues = (
  traveller: Traveller | null,
): TravellerFormValues =>
  traveller
    ? {
        title: traveller.title,
        firstName: traveller.firstName,
        lastName: traveller.lastName,
        emailAddress: traveller.emailAddress || "",
        phonePrefix: traveller.phonePrefix,
        phoneNumber: traveller.phonePrefix + traveller.phoneNumber,
        birthDate: new Date(traveller.birthDate),
        passportNumber: traveller.passportNumber,
        passportIssuingDate: new Date(traveller.passportIssuingDate),
        passportExpiryDate: new Date(traveller.passportExpiryDate),
        passportNationality: traveller.passportNationality,
        passportIssuingCountry: traveller.passportIssuingCountry,
      }
    : {
        title: "mr",
        firstName: "",
        lastName: "",
        birthDate: null,
        passportNumber: "",
        phonePrefix: "+964",
        passportIssuingDate: null,
        passportExpiryDate: null,
        emailAddress: "",
        phoneNumber: "",
        passportIssuingCountry: "IQ",
        passportNationality: "IQ",
      };

type UseTravellerFormOptions = {
  traveller: Traveller | null;
  onSubmit: (traveller: TravelerBodyInputDto) => Promise<unknown>;
  onClose: (reason: "cancel" | "cancelWithPendingChanges") => void;
  ageGroupConstraint?: TravellerAgeGroupConstraint;
};

export function useTravellerForm({
  traveller,
  onSubmit,
  onClose,
  ageGroupConstraint,
}: UseTravellerFormOptions) {
  const intl = useIntl();
  const [scanningDrawerOpen, setScanningDrawerOpen] = useState(false);
  const [scanned, setScanned] = useState(false);

  const ageGroup = ageGroupConstraint?.ageGroup;
  const travelDateTime = ageGroupConstraint?.travelDate.getTime();
  const requiredAgesKey = ageGroupConstraint?.requiredAges
    ? [...ageGroupConstraint.requiredAges].sort((a, b) => a - b).join(",")
    : undefined;

  const schema = useMemo(() => {
    const normalizedRequiredAges = requiredAgesKey
      ? requiredAgesKey.split(",").map(Number)
      : undefined;

    const normalizedAgeGroupConstraint =
      ageGroup && travelDateTime !== undefined
        ? ({
            ageGroup,
            travelDate: new Date(travelDateTime),
            requiredAges: normalizedRequiredAges,
          } satisfies TravellerAgeGroupConstraint)
        : undefined;

    return getTravellerValidationSchema(intl, normalizedAgeGroupConstraint);
  }, [intl, ageGroup, travelDateTime, requiredAgesKey]);

  const {
    reset,
    control,
    handleSubmit,
    getValues,
    setError,
    formState: { isDirty, isSubmitting },
  } = useForm<TravellerFormValues>({
    defaultValues: mapTravellerToFormValues(traveller),
    resolver: zodResolver(schema),
    reValidateMode: "onChange",
  });

  const requestClose = () => {
    onClose(isDirty ? "cancelWithPendingChanges" : "cancel");
  };

  const openScanningDrawer = () => {
    setScanningDrawerOpen(true);
    setScanned(false);
  };

  const closeScanningDrawer = () => {
    setScanningDrawerOpen(false);
  };

  const onScanSuccess = (data: ScannedData) => {
    setScanned(true);
    setScanningDrawerOpen(false);
    reset({
      ...getValues(),
      ...data,
      passportIssuingCountry: "IQ",
      passportNationality: "IQ",
    });
  };

  const onFormSubmit = handleSubmit((values) => {
    const {
      title,
      firstName,
      lastName,
      emailAddress,
      phonePrefix,
      phoneNumber,
      birthDate,
      passportNumber,
      passportIssuingDate,
      passportExpiryDate,
      passportNationality,
      passportIssuingCountry,
    } = values;

    return onSubmit({
      title,
      firstName,
      lastName,
      emailAddress: emailAddress || undefined,
      phonePrefix,
      phoneNumber: phoneNumber.replace(phonePrefix, ""),
      birthDate: dayjs(birthDate).format("YYYY-MM-DD"),
      passportNumber,
      passportIssuingDate: dayjs(passportIssuingDate).format("YYYY-MM-DD"),
      passportExpiryDate: dayjs(passportExpiryDate).format("YYYY-MM-DD"),
      passportNationality,
      passportIssuingCountry,
    })
      .then(() => {
        rudderAnalytics.track(CommonTrackingEvents.USER_SAVED_TRAVELER);
      })
      .catch((err) => {
        const isValidationError = (
          error: unknown,
        ): error is AxiosError<ValidationExceptionOutputDto> => {
          return (
            isAxiosError(error) &&
            error.response?.data?.code === "VALIDATION_FAILED"
          );
        };

        if (!isValidationError(err) || !err.response) {
          throw err;
        }

        const backendErrors = (
          err as {
            response?: {
              data: {
                data?: {
                  property: Path<TravellerFormValues>;
                  message: string;
                }[];
              };
            };
          }
        ).response?.data.data;

        if (!backendErrors) return;

        backendErrors.forEach(({ property, message }) => {
          setError(property, { type: "server", message });
        });
      });
  });

  return {
    control,
    isSubmitting,
    requestClose,
    scanned,
    scanningDrawerOpen,
    openScanningDrawer,
    closeScanningDrawer,
    onScanSuccess,
    onFormSubmit,
  };
}
